import { createFileRoute } from "@tanstack/react-router";
import { L, LegalAside, LegalPage, LegalSection, LList } from "@/components/LegalPage";

export const Route = createFileRoute("/terms")({
  head: () => {
    const title = "Terms — WHO KNEW";
    const description =
      "The agreement between WHO KNEW and anyone who buys our products or signs up for updates. Written in normal English.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/terms" },
      ],
    };
  },
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage eyebrow="TERMS" title="The agreement.">
      <LegalSection label="WHAT THIS IS" first>
        <L>
          These are the terms you agree to when you buy a <span className="text-burgundy">WHO KNEW</span> product or sign up
          for the mailing list. We've tried to write them in normal English. If anything
          is unclear, ask.
        </L>
        <L>By using the site or buying a product, you agree to what follows.</L>
      </LegalSection>

      <LegalSection label="WHAT YOU'RE BUYING">
        <L>
          When you pay for a <span className="text-burgundy">WHO KNEW</span> product, you get exactly what the product page
          describes — typically:
        </L>
        <LList
          items={[
            "Lifetime access to the materials you've bought.",
            "Lifetime access to any workbooks, templates, or downloadable assets that come with it.",
            "Any free updates we make to the same product in future.",
            "A 14-day refund window from the date of purchase.",
          ]}
        />
        <L>You don't get:</L>
        <LList
          items={[
            "A formal qualification, certificate, or accreditation.",
            "One-to-one tutoring or advice.",
            "A guarantee that you'll pass any exam, get any job, or achieve any specific outcome.",
          ]}
        />
        <L>
          We've made our best attempt at useful, honest material. What you do with it is
          up to you.
        </L>
      </LegalSection>

      <LegalSection label="USING WHAT YOU'VE BOUGHT">
        <L><span className="text-burgundy">WHO KNEW</span> products are for your personal use. You can:</L>
        <LList
          items={[
            "Use the materials as many times as you want.",
            "Download the workbooks, templates, and assets for your own use.",
            "Quote small bits to friends and colleagues when it helps.",
          ]}
        />
        <L>You can't:</L>
        <LList
          items={[
            "Share your account login with other people.",
            "Resell, redistribute, or rebroadcast the materials.",
            <>Strip the <span className="text-burgundy">WHO KNEW</span> branding off the workbooks, templates, or assets and present them as your own.</>
          ]}
        />
        <L>
          If you want to use the materials with a team, get in touch. We're reasonable.
        </L>
      </LegalSection>

      <LegalSection label="ACCOUNTS">
        <L>
          Your account is yours. Don't share it. If you suspect someone else has accessed
          it, tell us at hello@whoknew.guide and we'll sort it.
        </L>
      </LegalSection>

      <LegalSection label="WHAT WE'RE NOT RESPONSIBLE FOR">
        <L>
          <span className="text-burgundy">WHO KNEW</span> products are educational. They are not professional advice. Any
          decisions you make about your business, money, career, health, relationships,
          or anything else are yours. If you're making a serious decision, talk to a
          qualified professional, not a website.
        </L>
        <L>
          We've tried to make our materials accurate at the time of publication. Things
          change. Rules, laws, best practice, the world in general. If you spot an error,
          tell us.
        </L>
      </LegalSection>

      <LegalSection label="DISPUTES">
        <L>If something goes wrong, get in touch first. We'll try to sort it without lawyers.</L>
        <L>
          If we can't, these terms are governed by the law of England and Wales. Disputes
          go to the courts of England and Wales.
        </L>
      </LegalSection>

      <LegalSection label="CHANGES">
        <L>
          We may update these terms occasionally. When we do, we'll update the date at
          the top. If we change anything that affects existing customers, we'll tell you
          by email.
        </L>
        <LegalAside>
          We're not trying to catch you out. If you've read this far, thank you. Most
          people don't.
        </LegalAside>
      </LegalSection>
    </LegalPage>
  );
}
